import { useState, useEffect, useCallback, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import html2canvas from 'html2canvas';
import { jsPDF } from 'jspdf';
import { Save, Download, FileImage, FileText, Ruler, Loader2, Box, LayoutGrid, Undo2, Redo2, Magnet, History as HistoryIcon, FileSpreadsheet, Grid3x3, Drill, BookOpen, LayoutTemplate, Pencil, Check, X } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '@/components/ui/dropdown-menu';
import { Tooltip, TooltipContent, TooltipTrigger, TooltipProvider } from '@/components/ui/tooltip';
import ModuleLibrary from '@/components/ModuleLibrary';
import BuilderCanvas from '@/components/BuilderCanvas';
import CustomizationPanel from '@/components/CustomizationPanel';
import ThreeDPreview from '@/components/ThreeDPreview';
import HistoryPanel from '@/components/HistoryPanel';
import {
  calculateModuleCost, FILLING_PRESETS, MODULE_DEFINITIONS, INTERNAL_MODULE_TYPES,
  LOCAL_TEMPLATES, generateDesk, BODY_MODULE_TYPES,
} from '@/lib/furnitureData';
import { exportSpecificationXLSX } from '@/lib/specification';
import { exportNestingPDF } from '@/lib/nesting';
import { exportDrillingPDF } from '@/lib/drilling';
import { exportAssemblyPDF } from '@/lib/assembly';
import { makeSourceV2, isParametricSource } from '@/lib/parametric/schema';
import { compileSource, modulesToSourceV2 } from '@/lib/parametric/compiler';
import { validatePlacement } from '@/lib/constraints/engine';
import ParametricPanel from '@/components/ParametricPanel';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const genId = () => Math.random().toString(36).substr(2, 9);
const CONSTRAINTS_V2 = process.env.REACT_APP_CONSTRAINTS_V2 !== 'false';
const nowLabel = () => {
  const d = new Date();
  return `${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}:${String(d.getSeconds()).padStart(2, '0')}`;
};

const normalizeTableTemplateModules = (tpl) => {
  if (!tpl?.modules?.length) return tpl?.modules || [];
  const isTableCategory = tpl.category === 'table';
  const hasTabletop = tpl.modules.some(m => m.type === 'tabletop');
  if (!isTableCategory && !hasTabletop) return tpl.modules;

  const modules = tpl.modules;
  const tabletop = modules.find(m => m.type === 'tabletop');
  if (!tabletop) return modules;

  const legs = modules.filter(m => m.type === 'leg');
  const drawers = modules.filter(m => m.type === 'drawer');
  const crossbars = modules.filter(m => m.type === 'crossbar');
  const hasStructuralFrame = legs.length >= 4 && crossbars.length >= 2;
  const hasUnsupportedDrawers = drawers.length > 0 && crossbars.length === 0;

  if (hasStructuralFrame && !hasUnsupportedDrawers) return modules;

  const width = tabletop.width || 140;
  const depth = tabletop.depth || 70;
  const topH = tabletop.height || 4;
  const inferredLegH = Math.max(...legs.map(l => l.height || 0), 71);
  const height = inferredLegH + topH;
  const drawersCount = Math.min(3, Math.max(0, drawers.length));
  const color = tabletop.color_id || 'dark_oak';
  const material = tabletop.material || 'solid_wood';

  return generateDesk({
    width,
    depth,
    height,
    drawers: drawersCount,
    hasCrossbar: true,
    color,
    material,
  }).map(m => ({ ...m, rotation: m.rotation || 0 }));
};

const getFloorLevel = (modules) => {
  const anchors = modules.filter(m => BODY_MODULE_TYPES.includes(m.type) || m.type === 'leg');
  const source = anchors.length ? anchors : modules;
  return source.reduce((acc, m) => Math.max(acc, (m.y || 0) + (m.height || 0)), 0);
};

const clampModuleToFloor = (modules, moduleId, updates) => {
  const current = modules.find(m => m.id === moduleId);
  if (!current) return updates;

  const next = { ...current, ...updates };
  const floorY = getFloorLevel(modules);
  const maxY = Math.max(0, floorY - (next.height || current.height || 0));

  const normalized = { ...updates };
  const shouldNormalizeY = typeof updates.y === 'number' || typeof updates.height === 'number';

  if (typeof updates.x === 'number') {
    normalized.x = Math.max(0, updates.x);
  }
  if (shouldNormalizeY) {
    normalized.y = Math.min(maxY, Math.max(0, next.y || 0));
  }

  return normalized;
};

export default function BuilderPage() {
  const [searchParams] = useSearchParams();
  const templateId = searchParams.get('template');
  const configId = searchParams.get('config');

  const [furnitureType, setFurnitureType] = useState('wardrobe');
  const [modules, setModules] = useState([]);
  const [source, setSource] = useState(null);            // параметрический источник истины
  const [selectedCellKey, setSelectedCellKey] = useState(null);
  const [doorsOpen, setDoorsOpen] = useState(false);      // режим «открытые двери»
  const [techView, setTechView] = useState(false);        // технический вид с размерами
  const [selectedModuleId, setSelectedModuleId] = useState(null);
  const [scale, setScale] = useState(2);
  const [saveDialogOpen, setSaveDialogOpen] = useState(false);
  const [saveName, setSaveName] = useState('');
  const [draftSaveName, setDraftSaveName] = useState('');
  const [isEditingName, setIsEditingName] = useState(false);
  const [saving, setSaving] = useState(false);
  const [currentConfigId, setCurrentConfigId] = useState(null);
  const [viewMode, setViewMode] = useState('3d');
  const [snapEnabled, setSnapEnabled] = useState(true);
  const [templatesModalOpen, setTemplatesModalOpen] = useState(false);
  const showDimensions = true;
  const enforceNoOverlap = true;
  const [historyOpen, setHistoryOpen] = useState(false);

  // History: labelled undo/redo
  const undoStackRef = useRef([]);
  const redoStackRef = useRef([]);
  const [historyVersion, setHistoryVersion] = useState(0);
  const modulesRef = useRef([]);
  useEffect(() => { modulesRef.current = modules; }, [modules]);

  const pushHistory = useCallback((label, snapshot) => {
    undoStackRef.current.push({ id: genId(), label, time: nowLabel(), snapshot });
    if (undoStackRef.current.length > 60) undoStackRef.current.shift();
    redoStackRef.current = [];
    setHistoryVersion(v => v + 1);
  }, []);

  // Автопересчёт modules[] из параметрического source. Любое обновление source
  // моментально перерисовывает 3D, т. к. внизу рендер привязан к modules.
  useEffect(() => {
    if (!source) return;
    const validated = source.kind === 'parametric-v2'
      ? validatePlacement(source, { mode: 'soft' }).source
      : source;
    const compiled = compileSource(validated);
    const nextModules = compiled || [];
    setModules(nextModules);
    setSelectedModuleId((prev) => {
      if (!prev) return prev;
      return nextModules.some((m) => m.id === prev) ? prev : null;
    });
  }, [source]);

  // Хелпер: обновить source частично (с сохранением истории)
  const updateSource = useCallback((patch, label = 'Параметр') => {
    setSource(prev => {
      if (!prev) return prev;
      if (label) pushHistory(label, JSON.parse(JSON.stringify(modulesRef.current)));
      if (typeof patch === 'function') return patch(prev);
      return { ...prev, ...patch };
    });
  }, [pushHistory]);

  const mutateSourceParts = useCallback((label, mutate) => {
    setSource((prev) => {
      if (!prev || prev.kind !== 'parametric-v2') return prev;
      pushHistory(label, JSON.parse(JSON.stringify(modulesRef.current)));
      const nextParts = mutate(prev.parts || [], prev);
      return validatePlacement({ ...prev, parts: nextParts }, { mode: 'soft' }).source;
    });
  }, [pushHistory]);

  const undo = useCallback(() => {
    if (!undoStackRef.current.length) return;
    const prev = undoStackRef.current.pop();
    redoStackRef.current.push({ id: genId(), label: prev.label, time: nowLabel(),
      snapshot: JSON.parse(JSON.stringify(modulesRef.current)) });
    setModules(prev.snapshot);
    setSelectedModuleId(null);
    setHistoryVersion(v => v + 1);
  }, []);

  const redo = useCallback(() => {
    if (!redoStackRef.current.length) return;
    const next = redoStackRef.current.pop();
    undoStackRef.current.push({ id: genId(), label: next.label, time: nowLabel(),
      snapshot: JSON.parse(JSON.stringify(modulesRef.current)) });
    setModules(next.snapshot);
    setSelectedModuleId(null);
    setHistoryVersion(v => v + 1);
  }, []);

  const jumpHistory = useCallback((index) => {
    const entries = undoStackRef.current;
    if (index < 0 || index >= entries.length) return;
    const target = entries[index];
    const after = entries.slice(index + 1);
    undoStackRef.current = entries.slice(0, index);
    redoStackRef.current = [
      { id: genId(), label: 'Текущее', time: nowLabel(),
        snapshot: JSON.parse(JSON.stringify(modulesRef.current)) },
      ...after.reverse(),
    ];
    setModules(target.snapshot);
    setSelectedModuleId(null);
    setHistoryVersion(v => v + 1);
  }, []);

  const historyEntries = (() => {
    const entries = undoStackRef.current.map(e => ({ id: e.id, label: e.label, time: e.time, current: false }));
    entries.push({ id: 'current', label: 'Текущее состояние', time: nowLabel(), current: true });
    return entries;
  })();

  // Load template or config on mount
  useEffect(() => {
    if (templateId) {
      const loadLocal = () => {
        const local = LOCAL_TEMPLATES.find(t => t.id === templateId);
        if (local) {
          setFurnitureType(local.category);
          const normalized = normalizeTableTemplateModules(local);
          const nextModules = normalized.map(m => ({ ...m, id: genId(), rotation: m.rotation || 0 }));
          if (CONSTRAINTS_V2) setSource(modulesToSourceV2(nextModules, local.category));
          else setModules(nextModules);
          toast.success(`Шаблон "${local.name_ru}" загружен`);
          return true;
        }
        return false;
      };
      axios.get(`${API}/templates/${templateId}`).then(r => {
        const tpl = r.data;
        if (!tpl || !tpl.modules) {
          if (!loadLocal()) toast.error('Шаблон не найден');
          return;
        }
        setFurnitureType(tpl.category);
        const normalized = normalizeTableTemplateModules(tpl);
        const nextModules = normalized.map(m => ({ ...m, id: genId(), rotation: m.rotation || 0 }));
        if (CONSTRAINTS_V2) setSource(modulesToSourceV2(nextModules, tpl.category));
        else setModules(nextModules);
        toast.success(`Шаблон "${tpl.name_ru}" загружен`);
      }).catch(() => {
        if (!loadLocal()) toast.error('Не удалось загрузить шаблон');
      });
    } else if (configId) {
      axios.get(`${API}/configurations/${configId}`).then(r => {
        const cfg = r.data;
        setFurnitureType(cfg.furniture_type);
        const nextModules = cfg.modules.map(m => ({ ...m, id: m.id || genId(), rotation: m.rotation || 0 }));
        if (CONSTRAINTS_V2) setSource(modulesToSourceV2(nextModules, cfg.furniture_type));
        else setModules(nextModules);
        setCurrentConfigId(cfg.id);
        setSaveName(cfg.name);
        toast.success(`Проект "${cfg.name}" загружен`);
      }).catch(() => toast.error('Не удалось загрузить проект'));
    }
  }, [templateId, configId]);

  useEffect(() => {
    if (!CONSTRAINTS_V2) return;
    if (templateId || configId) return;
    setSource((prev) => prev || makeSourceV2({ category: furnitureType }));
  }, [templateId, configId, furnitureType]);

  const selectedModule = modules.find(m => m.id === selectedModuleId) || null;

  // Smart auto-placement
  const getSmartPosition = useCallback((type, defW, defH, currentModules) => {
    const body = currentModules.find(m => ['body', 'frame', 'tabletop', 'headboard', 'countertop', 'top_panel'].includes(m.type));
    const sameType = currentModules.filter(m => m.type === type);
    const n = sameType.length;
    if (!body) return { x: 10 + n * 20, y: 10 + n * 20 };
    switch (type) {
      case 'leg': {
        const positions = [
          { x: body.x + 4, y: body.y + body.height },
          { x: body.x + body.width - defW - 4, y: body.y + body.height },
          { x: body.x + Math.round(body.width * 0.35), y: body.y + body.height },
          { x: body.x + Math.round(body.width * 0.65) - defW, y: body.y + body.height },
        ];
        return positions[n % positions.length];
      }
      case 'door': {
        const dw = Math.min(defW, Math.round((body.width - 4) / Math.max(1, n + 1)));
        return { x: body.x + 1 + n * dw, y: body.y + 2 };
      }
      case 'shelf': case 'board': {
        const step = Math.round(body.height / (n + 3));
        return { x: body.x + 2, y: body.y + step * (n + 1) };
      }
      case 'drawer': {
        const dh = defH || 20;
        return { x: body.x + 2, y: body.y + body.height - (dh + 2) * (n + 1) };
      }
      case 'hanging_rod':
        return { x: body.x + 2, y: body.y + 20 + n * Math.round(body.height * 0.4) };
      case 'crossbar':
        return { x: body.x + 8, y: body.y + Math.round(body.height * 0.55) + n * 15 };
      case 'back_panel':
        return { x: body.x + 1, y: body.y + 1 };
      case 'bracket': {
        const positions = [
          { x: body.x + 8, y: body.y + 3 },
          { x: body.x + body.width - defW - 8, y: body.y + 3 },
        ];
        return positions[n % positions.length];
      }
      default:
        return { x: body.x + 5 + n * 12, y: body.y + 5 + n * 12 };
    }
  }, []);

  const addModule = useCallback((moduleDef, x, y) => {
    const defW = moduleDef.defaultW || moduleDef.width || 50;
    const defH = moduleDef.defaultH || moduleDef.height || 50;
    const pos = (x != null && y != null) ? { x, y }
      : getSmartPosition(moduleDef.type, defW, defH, modulesRef.current);
    if (source?.kind === 'parametric-v2') {
      mutateSourceParts(`Добавлен модуль: ${moduleDef.nameRu || moduleDef.name_ru || moduleDef.type}`, (parts) => {
        const body = parts.find((p) => !p.parentId) || parts[0];
        if (!body) return parts;
        const id = genId();
        return [...parts, {
          id,
          partType: moduleDef.type,
          parentId: body.id,
          socketRef: moduleDef.type === 'door' ? 'frontPlane' : 'innerContainer',
          localBox: {
            x: pos.x,
            y: pos.y,
            z: 0,
            w: defW,
            h: defH,
            d: moduleDef.defaultD || moduleDef.depth || 30,
          },
          attributes: {},
          constraints: { requiresSupport: ['shelf', 'board'].includes(moduleDef.type) },
        }];
      });
      return;
    }
    pushHistory(`Добавлен модуль: ${moduleDef.nameRu || moduleDef.name_ru || moduleDef.type}`,
      JSON.parse(JSON.stringify(modulesRef.current)));
    const newModule = {
      id: genId(),
      type: moduleDef.type,
      name_ru: moduleDef.nameRu || moduleDef.name_ru,
      width: defW,
      height: defH,
      depth: moduleDef.defaultD || moduleDef.depth || 30,
      x: pos.x, y: pos.y,
      rotation: 0,
      material: null,
      color_id: null,
      color_hex: '#BDBDBD',
      hardware: [],
      base_price: moduleDef.basePrice || moduleDef.base_price || 500,
    };
    setModules(prev => [...prev, newModule]);
    setSelectedModuleId(newModule.id);
  }, [pushHistory, getSmartPosition, source?.kind, mutateSourceParts]);

  // Debounced history for continuous edits (drag / resize / slider)
  const updateTimerRef = useRef(null);
  const preUpdateRef = useRef(null);
  const updateModule = useCallback((id, updates) => {
    if (source?.kind === 'parametric-v2') {
      mutateSourceParts('Изменение модуля', (parts) => parts.map((p) => {
        if (p.id !== id) return p;
        const b = p.localBox || {};
        const attr = p.attributes || {};
        const geometryKeys = new Set(['x', 'y', 'z_pos', 'width', 'height', 'depth', 'material', 'color_id']);
        const extraAttributes = Object.entries(updates).reduce((acc, [key, value]) => {
          if (!geometryKeys.has(key)) acc[key] = value;
          return acc;
        }, {});
        return {
          ...p,
          localBox: {
            ...b,
            x: updates.x ?? b.x,
            y: updates.y ?? b.y,
            z: updates.z_pos ?? b.z,
            w: updates.width ?? b.w,
            h: updates.height ?? b.h,
            d: updates.depth ?? b.d,
          },
          attributes: {
            ...attr,
            ...(updates.material !== undefined ? { body_material: updates.material } : {}),
            ...(updates.color_id !== undefined ? { body_color: updates.color_id } : {}),
            ...extraAttributes,
          },
        };
      }));
      return;
    }
    if (!preUpdateRef.current) {
      preUpdateRef.current = JSON.parse(JSON.stringify(modulesRef.current));
    }
    if (updateTimerRef.current) clearTimeout(updateTimerRef.current);
    updateTimerRef.current = setTimeout(() => {
      if (preUpdateRef.current) {
        const mod = modulesRef.current.find(m => m.id === id);
        const label = mod ? `Изменён: ${mod.name_ru}` : 'Изменение модуля';
        pushHistory(label, preUpdateRef.current);
        preUpdateRef.current = null;
      }
    }, 600);
    setModules(prev => {
      const safeUpdates = clampModuleToFloor(prev, id, updates);
      return prev.map(m => (m.id === id ? { ...m, ...safeUpdates } : m));
    });
  }, [pushHistory, source?.kind, mutateSourceParts]);

  const deleteModule = useCallback((id) => {
    if (source?.kind === 'parametric-v2') {
      mutateSourceParts('Удалён модуль', (parts) => parts.filter((p) => p.id !== id));
      if (selectedModuleId === id) setSelectedModuleId(null);
      return;
    }
    const mod = modulesRef.current.find(m => m.id === id);
    pushHistory(`Удалён: ${mod?.name_ru || 'модуль'}`, JSON.parse(JSON.stringify(modulesRef.current)));
    setModules(prev => prev.filter(m => m.id !== id));
    if (selectedModuleId === id) setSelectedModuleId(null);
  }, [selectedModuleId, pushHistory, source, mutateSourceParts]);

  const copyModule = useCallback((id) => {
    if (source?.kind === 'parametric-v2') {
      mutateSourceParts('Скопирован модуль', (parts) => {
        const p = parts.find((x) => x.id === id);
        if (!p) return parts;
        const b = p.localBox || {};
        return [...parts, { ...p, id: genId(), localBox: { ...b, x: (b.x || 0) + 10, y: (b.y || 0) + 10 } }];
      });
      return;
    }
    pushHistory('Скопирован модуль', JSON.parse(JSON.stringify(modulesRef.current)));
    const mod = modulesRef.current.find(m => m.id === id);
    if (!mod) return;
    const copy = { ...mod, id: genId(), x: mod.x + 10, y: mod.y + 10, name_ru: mod.name_ru + ' (копия)' };
    setModules(prev => [...prev, copy]);
    setSelectedModuleId(copy.id);
    toast.success('Модуль скопирован');
  }, [pushHistory, source, mutateSourceParts]);

  const rotateModule = useCallback((id) => {
    if (source?.kind === 'parametric-v2') {
      return;
    }
    const mod = modulesRef.current.find(m => m.id === id);
    if (!mod) return;
    pushHistory(`Повёрнут: ${mod.name_ru}`, JSON.parse(JSON.stringify(modulesRef.current)));
    const cur = ((mod.rotation || 0) + 90) % 360;
    setModules(prev => prev.map(m => m.id === id ? { ...m, rotation: cur } : m));
  }, [pushHistory, source]);

  const replaceModule = useCallback((id, newType) => {
    pushHistory('Заменён модуль', JSON.parse(JSON.stringify(modulesRef.current)));
    const defs = MODULE_DEFINITIONS[furnitureType] || [];
    const def = defs.find(d => d.type === newType);
    if (!def) return;
    setModules(prev => prev.map(m => m.id === id ? {
      ...m, type: newType, name_ru: def.nameRu, base_price: def.basePrice,
    } : m));
    toast.success(`Модуль заменён на "${def.nameRu}"`);
  }, [pushHistory, furnitureType]);

  const applyFilling = useCallback((presetId, bodyModule) => {
    if (!bodyModule) return;
    pushHistory('Применено наполнение', JSON.parse(JSON.stringify(modulesRef.current)));
    const preset = FILLING_PRESETS.find(p => p.id === presetId);
    if (!preset) return;
    setModules(prev => {
      const cleaned = prev.filter(m =>
        m.id === bodyModule.id || !INTERNAL_MODULE_TYPES.includes(m.type) || m.type === 'back_panel'
      );
      const newMods = [];

      if (preset.grid) {
        const { cols, rows } = preset.grid;
        const t = 2;
        const innerW = bodyModule.width - 4;
        const innerH = bodyModule.height - 4;
        const cellW = (innerW - (cols - 1) * t) / cols;
        const cellH = (innerH - (rows - 1) * t) / rows;
        const baseShared = {
          rotation: 0,
          material: bodyModule.material,
          color_id: bodyModule.color_id,
          color_hex: bodyModule.color_hex,
          hardware: [],
        };
        for (let i = 1; i < cols; i++) {
          newMods.push({
            id: genId(), type: 'shelf', name_ru: `Перегородка ${i}`,
            width: t, height: innerH, depth: bodyModule.depth - 2,
            x: bodyModule.x + 2 + i * (cellW + t) - t,
            y: bodyModule.y + 2,
            base_price: 450, ...baseShared,
          });
        }
        for (let j = 1; j < rows; j++) {
          newMods.push({
            id: genId(), type: 'shelf', name_ru: `Полка ${j}`,
            width: innerW, height: t, depth: bodyModule.depth - 2,
            x: bodyModule.x + 2,
            y: bodyModule.y + 2 + j * (cellH + t) - t,
            base_price: 400, ...baseShared,
          });
        }
      } else {
        (preset.modules || []).forEach(pm => {
          const w = bodyModule.width - 4;
          const h = pm.relH ? Math.round(bodyModule.height * pm.relH) : (pm.type === 'drawer' ? 20 : 2);
          newMods.push({
            id: genId(),
            type: pm.type,
            name_ru: pm.type === 'shelf' ? 'Полка' : pm.type === 'drawer' ? 'Ящик' :
              pm.type === 'hanging_rod' ? 'Штанга' : pm.type,
            width: w, height: h,
            depth: pm.type === 'hanging_rod' ? 3 : bodyModule.depth - 2,
            x: bodyModule.x + 2,
            y: bodyModule.y + Math.round(bodyModule.height * pm.relY),
            rotation: 0,
            material: pm.type === 'hanging_rod' ? 'metal' : bodyModule.material,
            color_id: pm.type === 'hanging_rod' ? 'gray' : bodyModule.color_id,
            color_hex: pm.type === 'hanging_rod' ? '#9E9E9E' : bodyModule.color_hex,
            hardware: pm.type === 'drawer' ? ['handle_modern', 'slide_ball'] : [],
            base_price: pm.type === 'shelf' ? 500 : pm.type === 'drawer' ? 1500 : 400,
          });
        });
      }

      return [...cleaned, ...newMods];
    });
    toast.success(`Наполнение "${preset.nameRu}" применено`);
  }, [pushHistory]);

  const applyTemplate = useCallback((tpl) => {
    pushHistory(`Загружен шаблон: ${tpl.name_ru}`, JSON.parse(JSON.stringify(modulesRef.current)));
    const normalized = normalizeTableTemplateModules(tpl);
    const nextModules = normalized.map(m => ({ ...m, id: genId(), rotation: m.rotation || 0 }));
    if (CONSTRAINTS_V2) setSource(modulesToSourceV2(nextModules, tpl.category));
    else setModules(nextModules);
    setSelectedModuleId(null);
    toast.success(`Шаблон "${tpl.name_ru}" применён`);
  }, [pushHistory]);

  const generateParametric = useCallback((newModules) => {
    pushHistory('Сгенерирована параметрическая конструкция',
      JSON.parse(JSON.stringify(modulesRef.current)));
    if (CONSTRAINTS_V2 && Array.isArray(newModules)) {
      setSource(modulesToSourceV2(newModules, furnitureType));
    } else {
      setModules(newModules);
    }
    setSelectedModuleId(null);
    toast.success('Конструкция сгенерирована');
  }, [pushHistory, furnitureType]);

  // Keyboard shortcuts
  useEffect(() => {
    const onKey = (e) => {
      if (e.target.tagName === 'INPUT' || e.target.tagName === 'TEXTAREA') return;
      if ((e.ctrlKey || e.metaKey) && e.key === 'z') {
        e.preventDefault();
        e.shiftKey ? redo() : undo();
      }
      if ((e.ctrlKey || e.metaKey) && e.key === 'c' && selectedModuleId) {
        e.preventDefault();
        copyModule(selectedModuleId);
      }
      if (e.key === 'Delete' && selectedModuleId) {
        deleteModule(selectedModuleId);
      }
      if (e.key.toLowerCase() === 'r' && selectedModuleId && !e.ctrlKey && !e.metaKey) {
        rotateModule(selectedModuleId);
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [undo, redo, copyModule, deleteModule, rotateModule, selectedModuleId]);

  // Save
  const handleSave = async () => {
    if (!saveName.trim()) return;
    setSaving(true);
    try {
      const payload = {
        name: saveName.trim(),
        furniture_type: furnitureType,
        modules,
        total_price: modules.reduce((sum, m) => sum + calculateModuleCost(m), 0),
      };
      if (currentConfigId) {
        await axios.put(`${API}/configurations/${currentConfigId}`, payload);
        toast.success('Проект обновлён');
      } else {
        const res = await axios.post(`${API}/configurations`, payload);
        setCurrentConfigId(res.data.id);
        toast.success('Проект сохранён');
      }
      setSaveDialogOpen(false);
    } catch {
      toast.error('Ошибка сохранения');
    }
    setSaving(false);
  };

  const exportSpec = () => {
    if (!modules.length) {
      toast.error('Нет деталей для спецификации');
      return;
    }
    try {
      exportSpecificationXLSX(modules, { projectName: saveName || 'Проект' });
      toast.success('Спецификация XLSX сформирована');
    } catch (e) {
      console.error(e);
      toast.error('Ошибка формирования спецификации');
    }
  };

  const exportNesting = async () => {
    if (!modules.length) {
      toast.error('Нет деталей для раскроя');
      return;
    }
    try {
      toast.loading('Формирую карту раскроя…', { id: 'nesting' });
      const { sheetsCount } = await exportNestingPDF(modules, { projectName: saveName || 'Проект' });
      if (sheetsCount === 0) {
        toast.error('Нет листовых деталей для раскроя', { id: 'nesting' });
      } else {
        toast.success(`Карта раскроя: ${sheetsCount} лист(ов)`, { id: 'nesting' });
      }
    } catch (e) {
      console.error(e);
      toast.error('Ошибка формирования карты раскроя', { id: 'nesting' });
    }
  };

  const exportAssembly = async () => {
    if (!modules.length) {
      toast.error('Нет деталей для инструкции');
      return;
    }
    try {
      toast.loading('Формирую инструкцию по сборке…', { id: 'assembly' });
      const { steps } = await exportAssemblyPDF(modules, { projectName: saveName || 'Проект' });
      if (!steps || steps.length === 0) {
        toast.error('Не удалось построить сценарий сборки', { id: 'assembly' });
      } else {
        toast.success(`Инструкция: ${steps.length} шагов`, { id: 'assembly' });
      }
    } catch (e) {
      console.error(e);
      toast.error('Ошибка формирования инструкции', { id: 'assembly' });
    }
  };

  const exportDrilling = async () => {
    if (!modules.length) {
      toast.error('Нет деталей для присадки');
      return;
    }
    try {
      toast.loading('Формирую карту присадки…', { id: 'drilling' });
      const { parts } = await exportDrillingPDF(modules, { projectName: saveName || 'Проект' });
      if (!parts || parts.length === 0) {
        toast.error('Нет деталей с присадкой', { id: 'drilling' });
      } else {
        toast.success(`Карта присадки: ${parts.length} деталь(ей)`, { id: 'drilling' });
      }
    } catch (e) {
      console.error(e);
      toast.error('Ошибка формирования карты присадки', { id: 'drilling' });
    }
  };

  const exportCanvas = async (type) => {
    const el = document.getElementById('builder-canvas');
    if (!el) return;
    try {
      if (type === 'blueprint') el.classList.add('blueprint-mode');
      const canvas = await html2canvas(el, { backgroundColor: type === 'blueprint' ? '#ffffff' : '#F4F1ED', scale: 2 });
      if (type === 'blueprint') el.classList.remove('blueprint-mode');
      if (type === 'pdf' || type === 'blueprint') {
        const imgData = canvas.toDataURL('image/png');
        const pdf = new jsPDF('l', 'mm', 'a4');
        const pw = pdf.internal.pageSize.getWidth();
        const ph = pdf.internal.pageSize.getHeight();
        const ratio = Math.min(pw / canvas.width, ph / canvas.height) * 0.9;
        const x = (pw - canvas.width * ratio) / 2;
        const y = (ph - canvas.height * ratio) / 2;
        pdf.addImage(imgData, 'PNG', x, y, canvas.width * ratio, canvas.height * ratio);
        pdf.save(type === 'blueprint' ? 'blueprint.pdf' : 'furniture-design.pdf');
        toast.success(type === 'blueprint' ? 'Чертёж экспортирован' : 'PDF экспортирован');
      } else {
        const link = document.createElement('a');
        link.download = 'furniture-design.png';
        link.href = canvas.toDataURL();
        link.click();
        toast.success('Изображение экспортировано');
      }
    } catch {
      toast.error('Ошибка экспорта');
    }
  };

  const canUndo = undoStackRef.current.length > 0;
  const canRedo = redoStackRef.current.length > 0;

  const startEditingName = () => {
    setDraftSaveName(saveName || 'Новый проект');
    setIsEditingName(true);
  };

  const cancelEditingName = () => {
    setDraftSaveName('');
    setIsEditingName(false);
  };

  const submitEditingName = async () => {
    const trimmedName = draftSaveName.trim();
    if (!trimmedName) {
      toast.error('Введите название проекта');
      return;
    }

    if (trimmedName === saveName) {
      cancelEditingName();
      return;
    }

    setSaving(true);
    try {
      setSaveName(trimmedName);
      if (currentConfigId) {
        const payload = {
          name: trimmedName,
          furniture_type: furnitureType,
          modules,
          total_price: modules.reduce((sum, m) => sum + calculateModuleCost(m), 0),
        };
        await axios.put(`${API}/configurations/${currentConfigId}`, payload);
        toast.success('Название проекта сохранено');
      } else {
        toast.success('Название обновлено. Нажмите "Сохранить" для записи проекта');
      }
      cancelEditingName();
    } catch {
      toast.error('Не удалось сохранить название проекта');
    }
    setSaving(false);
  };

  return (
    <div className="flex flex-col" style={{ height: 'calc(100vh - 64px)' }} data-testid="builder-page">
      <div className="h-12 px-4 flex items-center justify-between bg-[#FAFAFA] border-b border-[#E0DDD8] shrink-0">
        <div className="flex items-center gap-8">
          <div className="flex items-center gap-2">
            {isEditingName ? (
              <>
                <Input
                  value={draftSaveName}
                  onChange={(e) => setDraftSaveName(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') submitEditingName();
                    if (e.key === 'Escape') cancelEditingName();
                  }}
                  className="h-8 rounded-sm text-sm border-[#E0DDD8] min-w-[180px]"
                  data-testid="project-name-inline-input"
                />
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={submitEditingName}
                  disabled={saving}
                  className="h-8 w-8 p-0 rounded-sm text-[#C25934] hover:bg-[#C25934]/10"
                  data-testid="project-name-inline-save-btn"
                >
                  {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Check className="w-4 h-4" />}
                </Button>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={cancelEditingName}
                  disabled={saving}
                  className="h-8 w-8 p-0 rounded-sm text-[#5C5C5C] hover:bg-[#E8E5E1]"
                  data-testid="project-name-inline-cancel-btn"
                >
                  <X className="w-4 h-4" />
                </Button>
              </>
            ) : (
              <>
                <span className="text-sm font-semibold text-[#1C1C1C]" style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}>
                  {saveName || 'Новый проект'}
                </span>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={startEditingName}
                  className="h-8 w-8 p-0 rounded-sm text-[#5C5C5C] hover:bg-[#E8E5E1]"
                  data-testid="project-name-edit-btn"
                >
                  <Pencil className="w-4 h-4" />
                </Button>
              </>
            )}
          </div>
          <Button
            variant="outline"
            size="sm"
            onClick={() => setTemplatesModalOpen(true)}
            className="rounded-sm text-xs h-8 border-[#C25934]/40 text-[#C25934] hover:bg-[#C25934]/10"
            data-testid="open-templates-modal-btn-header"
          >
            <LayoutTemplate className="w-3.5 h-3.5 mr-1.5" /> Готовые конструкции
          </Button>
        </div>
        <div className="flex items-center gap-1.5">
          <TooltipProvider delayDuration={300}>
            <Tooltip>
              <TooltipTrigger asChild>
                <Button variant="ghost" size="sm" onClick={undo} disabled={!canUndo}
                  className="h-8 w-8 p-0 rounded-sm" data-testid="undo-btn">
                  <Undo2 className="w-4 h-4" />
                </Button>
              </TooltipTrigger>
              <TooltipContent side="bottom"><span className="text-xs">Отменить (Ctrl+Z)</span></TooltipContent>
            </Tooltip>
            <Tooltip>
              <TooltipTrigger asChild>
                <Button variant="ghost" size="sm" onClick={redo} disabled={!canRedo}
                  className="h-8 w-8 p-0 rounded-sm" data-testid="redo-btn">
                  <Redo2 className="w-4 h-4" />
                </Button>
              </TooltipTrigger>
              <TooltipContent side="bottom"><span className="text-xs">Повторить (Ctrl+Shift+Z)</span></TooltipContent>
            </Tooltip>
            <Tooltip>
              <TooltipTrigger asChild>
                <Button variant="ghost" size="sm"
                  onClick={() => setHistoryOpen(o => !o)}
                  className={`h-8 w-8 p-0 rounded-sm ${historyOpen ? 'text-[#C25934] bg-[#C25934]/10' : ''}`}
                  data-testid="history-btn">
                  <HistoryIcon className="w-4 h-4" />
                </Button>
              </TooltipTrigger>
              <TooltipContent side="bottom"><span className="text-xs">История операций</span></TooltipContent>
            </Tooltip>
            <Tooltip>
              <TooltipTrigger asChild>
                <Button variant="ghost" size="sm"
                  onClick={() => setSnapEnabled(s => !s)}
                  className={`h-8 w-8 p-0 rounded-sm ${snapEnabled ? 'text-[#C25934] bg-[#C25934]/10' : ''}`}
                  data-testid="snap-toggle-btn">
                  <Magnet className="w-4 h-4" />
                </Button>
              </TooltipTrigger>
              <TooltipContent side="bottom"><span className="text-xs">Привязка к сетке/краям {snapEnabled ? '(вкл)' : '(выкл)'}</span></TooltipContent>
            </Tooltip>
          </TooltipProvider>

          <div className="w-px h-6 bg-[#E0DDD8] mx-1" />

          <div className="flex items-center bg-[#E8E5E1] rounded-sm p-0.5 mr-2" data-testid="view-mode-toggle">
            <button
              onClick={() => setViewMode('2d')}
              className={`flex items-center gap-1 px-2.5 py-1 rounded-sm text-xs font-medium transition-colors ${
                viewMode === '2d' ? 'bg-white text-[#C25934] shadow-sm' : 'text-[#5C5C5C] hover:text-[#1C1C1C]'
              }`}
              data-testid="view-2d-btn"
            >
              <LayoutGrid className="w-3.5 h-3.5" /> 2D
            </button>
            <button
              onClick={() => setViewMode('3d')}
              className={`flex items-center gap-1 px-2.5 py-1 rounded-sm text-xs font-medium transition-colors ${
                viewMode === '3d' ? 'bg-white text-[#C25934] shadow-sm' : 'text-[#5C5C5C] hover:text-[#1C1C1C]'
              }`}
              data-testid="view-3d-btn"
            >
              <Box className="w-3.5 h-3.5" /> 3D
            </button>
          </div>

          <Button
            variant="outline" size="sm"
            onClick={() => setSaveDialogOpen(true)}
            className="rounded-sm text-xs h-8 border-[#E0DDD8] hover:bg-[#E8E5E1]"
            data-testid="save-btn"
          >
            <Save className="w-3.5 h-3.5 mr-1.5" /> Сохранить
          </Button>
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="outline" size="sm"
                className="rounded-sm text-xs h-8 border-[#E0DDD8] hover:bg-[#E8E5E1]" data-testid="export-btn">
                <Download className="w-3.5 h-3.5 mr-1.5" /> Экспорт
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuItem onClick={() => exportCanvas('image')} data-testid="export-image">
                <FileImage className="w-4 h-4 mr-2" /> Изображение PNG
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => exportCanvas('pdf')} data-testid="export-pdf">
                <FileText className="w-4 h-4 mr-2" /> Документ PDF
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => exportCanvas('blueprint')} data-testid="export-blueprint">
                <Ruler className="w-4 h-4 mr-2" /> Чертёж
              </DropdownMenuItem>
              <DropdownMenuItem onClick={exportSpec} data-testid="export-spec-xlsx">
                <FileSpreadsheet className="w-4 h-4 mr-2" /> Спецификация XLSX
              </DropdownMenuItem>
              <DropdownMenuItem onClick={exportNesting} data-testid="export-nesting-pdf">
                <Grid3x3 className="w-4 h-4 mr-2" /> Карта раскроя PDF
              </DropdownMenuItem>
              <DropdownMenuItem onClick={exportDrilling} data-testid="export-drilling-pdf">
                <Drill className="w-4 h-4 mr-2" /> Карта присадки PDF
              </DropdownMenuItem>
              <DropdownMenuItem onClick={exportAssembly} data-testid="export-assembly-pdf">
                <BookOpen className="w-4 h-4 mr-2" /> Инструкция по сборке PDF
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </div>

      <div className="flex flex-1 overflow-hidden">
        <ModuleLibrary
          furnitureType={furnitureType}
          onChangeFurnitureType={(type) => {
            pushHistory('Смена типа мебели', JSON.parse(JSON.stringify(modulesRef.current)));
            setFurnitureType(type);
            if (CONSTRAINTS_V2) {
              setSource(makeSourceV2({ category: type }));
            } else {
              setSource(null);
              setModules([]);
            }
            setSelectedModuleId(null);
          }}
          onAddModule={(def) => addModule(def)}
          onApplyTemplate={applyTemplate}
          onGenerateParametric={generateParametric}
          templatesModalOpen={templatesModalOpen}
          onTemplatesModalOpenChange={setTemplatesModalOpen}
          showTemplatesTrigger={false}
        />

        {viewMode === '2d' ? (
          <BuilderCanvas
            modules={modules}
            selectedId={selectedModuleId}
            scale={scale}
            snapEnabled={snapEnabled}
            showDimensions={showDimensions}
            enforceNoOverlap={enforceNoOverlap}
            onSelect={setSelectedModuleId}
            onUpdateModule={updateModule}
            onAddModuleAtPosition={(def, x, y) => addModule(def, x, y)}
            onDeleteModule={deleteModule}
            onCopyModule={copyModule}
            onRotateModule={rotateModule}
            onScaleChange={setScale}
          />
        ) : (
          <ThreeDPreview
            modules={modules}
            selectedId={selectedModuleId}
            snapEnabled={snapEnabled}
            onSelect={setSelectedModuleId}
            onUpdateModule={updateModule}
            onAddModuleAtPosition={(def, x, y) => addModule(def, x, y)}
            onDeleteModule={deleteModule}
            onCopyModule={copyModule}
            onRotateModule={rotateModule}
            doorsOpen={doorsOpen}
            techView={techView}
          />
        )}

        {source && source.kind !== 'parametric-v2' ? (
          <ParametricPanel
            source={source}
            selectedCellKey={selectedCellKey}
            onSelectCell={setSelectedCellKey}
            onChange={(next, label) => updateSource(next, label)}
            doorsOpen={doorsOpen}
            onToggleDoors={() => setDoorsOpen(v => !v)}
            techView={techView}
            onToggleTechView={() => setTechView(v => !v)}
          />
        ) : (
          <CustomizationPanel
            selectedModule={selectedModule}
            modules={modules}
            furnitureType={furnitureType}
            selectedModuleId={selectedModuleId}
            onSelectModule={setSelectedModuleId}
            onUpdateModule={updateModule}
            onDeleteModule={deleteModule}
            onCopyModule={copyModule}
            onRotateModule={rotateModule}
            onReplaceModule={replaceModule}
            onApplyFilling={applyFilling}
          />
        )}
      </div>

      <HistoryPanel
        open={historyOpen}
        onClose={() => setHistoryOpen(false)}
        entries={historyEntries}
        onJump={jumpHistory}
        key={historyVersion}
      />

      <Dialog open={saveDialogOpen} onOpenChange={setSaveDialogOpen}>
        <DialogContent className="sm:max-w-md" data-testid="save-dialog">
          <DialogHeader>
            <DialogTitle style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}>
              Сохранить проект
            </DialogTitle>
          </DialogHeader>
          <div className="py-4">
            <label className="text-xs text-[#5C5C5C] mb-1.5 block">Название проекта</label>
            <Input
              value={saveName}
              onChange={(e) => setSaveName(e.target.value)}
              placeholder="Мой шкаф..."
              className="rounded-sm border-[#E0DDD8]"
              data-testid="save-name-input"
            />
          </div>
          <DialogFooter>
            <Button
              onClick={handleSave}
              disabled={!saveName.trim() || saving}
              className="bg-[#C25934] hover:bg-[#A64A2B] text-white rounded-sm"
              data-testid="save-confirm-btn"
            >
              {saving ? <Loader2 className="w-4 h-4 animate-spin mr-1.5" /> : <Save className="w-4 h-4 mr-1.5" />}
              {currentConfigId ? 'Обновить' : 'Сохранить'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
